import React, { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import CommentHistory from './CommentHistory';
import {
  findFieldInFormSection,
  getFieldAnswerValue,
  getHiddenFieldNotice,
  isFieldVisibleInReadOnlyView,
  resolveFieldDisplayLabel,
} from './queryFieldNavigation';

const MASK_FIELD_KEY = [
  'subjectName',
  'subjectNameKey',
  'subjectAddress',
  'subjectEmail',
  'subjectPhone',
  'subjectMobile',
  'accountNumber',
  'confirmAccountNumber',
  'routingNumber',
  'abaNumber',
  'swiftCode',
  'ssn',
  'firstName',
  'lastName',
  'selectGender',
  'dobBankDetails',
  'subjectEmail_BD',
  'subjectMobile_BD',
  'subjectAddress_BD',
  'subjectCity_BD',
  'subjectState_BD',
  'subjectZipcode_BD',
  'countrycode_BD',
  'governmentDocuments',
];

/**
 * Opens query/comment/history drawer when URL targets a field that is not rendered in read-only view.
 */
const HiddenFieldQueryDrawer = ({
  sectionDetails,
  answers,
  userSiteTrialRole,
  formConfig,
  sectionKey,
  subSectionKey,
  stepKey,
  formLabel,
  ...rest
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const queryFieldKey = searchParams.get('fieldKey');
  const queryCategoryKey = searchParams.get('categoryKey');

  const resolved = useMemo(() => {
    if (!queryFieldKey || !sectionDetails) return null;
    return findFieldInFormSection(sectionDetails, queryFieldKey, queryCategoryKey);
  }, [queryFieldKey, queryCategoryKey, sectionDetails]);

  const categoryForResolved = useMemo(() => {
    if (!resolved?.categoryKey || !sectionDetails?.categories) return null;
    return sectionDetails.categories.find((c) => c.key === resolved.categoryKey) || null;
  }, [resolved, sectionDetails]);

  const isQueryNavigation = Boolean(
    searchParams.get('queryId') || searchParams.get('tab') === 'query'
  );

  const shouldOpenFallbackDrawer = useMemo(() => {
    if (!queryFieldKey || !isQueryNavigation) return false;
    if (!resolved?.field) return true;
    return !isFieldVisibleInReadOnlyView(
      resolved.field,
      answers,
      userSiteTrialRole,
      categoryForResolved
    );
  }, [
    queryFieldKey,
    isQueryNavigation,
    resolved,
    answers,
    userSiteTrialRole,
    categoryForResolved,
  ]);

  const hiddenFieldNotice = useMemo(() => {
    if (!shouldOpenFallbackDrawer) return null;
    if (!resolved?.field) {
      const fieldLabel = resolveFieldDisplayLabel(
        { key: queryFieldKey },
        formConfig,
        sectionDetails,
        queryCategoryKey
      );
      return `The field "${fieldLabel}" is not shown in read-only view on this form. You can still view and manage queries below.`;
    }
    return getHiddenFieldNotice(
      resolved.field,
      answers,
      userSiteTrialRole,
      categoryForResolved,
      sectionDetails,
      formConfig,
      resolved?.categoryKey || queryCategoryKey
    );
  }, [
    shouldOpenFallbackDrawer,
    resolved,
    queryFieldKey,
    queryCategoryKey,
    answers,
    userSiteTrialRole,
    categoryForResolved,
    sectionDetails,
    formConfig,
  ]);

  const fieldForDrawer = useMemo(() => {
    if (!shouldOpenFallbackDrawer) return null;
    if (resolved?.field) {
      return {
        ...resolved.field,
        label: resolveFieldDisplayLabel(
          resolved.field,
          formConfig,
          sectionDetails,
          resolved.categoryKey
        ),
      };
    }
    return {
      key: queryFieldKey,
      label: resolveFieldDisplayLabel(
        { key: queryFieldKey },
        formConfig,
        sectionDetails,
        queryCategoryKey
      ),
    };
  }, [shouldOpenFallbackDrawer, resolved, queryFieldKey, queryCategoryKey, formConfig, sectionDetails]);

  const answer = useMemo(() => {
    if (!fieldForDrawer) return undefined;
    return getFieldAnswerValue(answers, resolved?.categoryKey, fieldForDrawer.key);
  }, [fieldForDrawer, answers, resolved]);

  const onCloseHistory = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('fieldKey');
    params.delete('categoryKey');
    params.delete('tab');
    params.delete('queryId');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  if (!shouldOpenFallbackDrawer || !fieldForDrawer) {
    return null;
  }

  return (
    <CommentHistory
      open
      onCloseHistory={onCloseHistory}
      field={fieldForDrawer}
      answer={answer}
      sectionKey={sectionKey}
      subSectionKey={subSectionKey}
      stepKey={stepKey}
      categoryKey={resolved?.categoryKey}
      formConfig={formConfig}
      formLabel={formLabel}
      hiddenFieldNotice={hiddenFieldNotice}
      MASK_FIELD_KEY={MASK_FIELD_KEY}
      userSiteTrialRole={userSiteTrialRole}
      {...rest}
    />
  );
};

export default React.memo(HiddenFieldQueryDrawer);
